import { useTranslation } from 'react-i18next';
import type { RegistrationBlockData } from '@foco/contracts';
import { formatDate as formatLocaleDate, formatCurrency } from '../../../shared/lib/format';
import { CopyableValue, Field, FieldGrid, useLocale } from './primitives';

/**
 * Registration data — the anchor block of the card.
 *
 * Every value here is a candidate for "copy", because a user checking a
 * counterparty pastes these into a contract, an invoice or a bank form, and
 * typing a 14-digit CNPJ by hand is how errors get made.
 */
export function RegistrationBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const block = (data ?? {}) as RegistrationBlockData;

  return (
    <>
      <FieldGrid>
        <Field label={t('blocks.registration.legalName')}>
          <CopyableValue value={block.legalName} />
        </Field>

        <Field label={t('blocks.registration.tradeName')}>
          <CopyableValue value={block.tradeName} />
        </Field>

        <Field label={t('blocks.registration.status')}>
          <StatusPill status={block.status} raw={block.statusReason} />
        </Field>

        <Field label={t('blocks.registration.registrationDate')}>
          <CopyableValue value={formatLocaleDate(block.registrationDate, locale)} />
        </Field>

        <Field label={t('blocks.registration.legalForm')}>
          <CopyableValue value={block.legalForm} />
        </Field>

        <Field label={t('blocks.registration.capital')}>
          <CopyableValue
            value={
              block.capitalAmount === null || block.capitalAmount === undefined
                ? null
                : formatCurrency(block.capitalAmount, block.capitalCurrency ?? 'USD', locale)
            }
          />
        </Field>

        <Field label={t('blocks.registration.size')}>
          <CopyableValue value={block.size} />
        </Field>

        {block.isSimples !== null && (
          <Field label={t('blocks.registration.simples')}>
            {block.isSimples ? t('pricing.yes') : t('pricing.no')}
          </Field>
        )}

        {block.isMei !== null && (
          <Field label={t('blocks.registration.mei')}>
            {block.isMei ? t('pricing.yes') : t('pricing.no')}
          </Field>
        )}
      </FieldGrid>

      {block.identifiers?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-fg)]">
            {t('blocks.registration.identifiers')}
          </p>
          <ul className="mt-1.5 space-y-1 text-sm">
            {block.identifiers.map((identifier) => (
              <li key={`${identifier.idType}-${identifier.value}`} className="flex gap-2">
                <span className="w-14 shrink-0 text-[var(--color-muted-fg)]">
                  {identifier.idType.toUpperCase()}
                </span>
                <CopyableValue value={identifier.value} mono />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * The registry status, coloured *and* labelled.
 *
 * Colour alone is not an accessible signal, and this is the single most
 * consequential field on the card — telling a user a struck-off company is
 * "active" is the worst output the product can produce.
 */
export function StatusPill({ status, raw }: { status: string | null; raw?: string | null }): React.ReactNode {
  const { t } = useTranslation();

  const tone =
    status === 'active'
      ? 'bg-risk-low/10 text-risk-low border-risk-low/30'
      : status === 'closed'
        ? 'bg-risk-high/10 text-risk-high border-risk-high/30'
        : status === 'suspended'
          ? 'bg-risk-medium/10 text-risk-medium border-risk-medium/30'
          : 'bg-[var(--color-surface-2)] text-[var(--color-muted-fg)] border-[var(--color-border)]';

  const label =
    status === 'active'
      ? t('status.active', 'Active')
      : status === 'closed'
        ? t('status.closed', 'Closed')
        : status === 'suspended'
          ? t('status.suspended', 'Suspended')
          : t('status.unknown', 'Unknown');

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone}`}>
        {label}
      </span>
      {/* The registry's own wording is kept: a user comparing against the
          official site must see the same string the registry published. */}
      {raw && raw !== label && (
        <span className="text-xs text-[var(--color-muted-fg)]">{raw}</span>
      )}
    </span>
  );
}

